import { runConvexMutation, runConvexQuery } from '@/lib/convex';

// Slice 2 (2026-04-27 acquisition reset) frontend service for the Kickstart AI Discovery
// Blueprint. Wraps the four Convex functions in convex/kickstartBlueprint.ts behind a
// small typed surface the V2 LessonPage / BreakthroughPage / BridgePage can call.
//
// Codebase convention: this surface uses the HTTP Convex client via runConvexQuery /
// runConvexMutation with a Clerk token from useAppAuth().getToken(). The codebase does
// not use convex/react hooks; do not introduce them here without a parallel ConvexProvider
// boot.

export type KickstartBlueprintWrapper =
  | 'reward_funnel'
  | 'vsl_application'
  | 'checkout_with_bumps'
  | 'short_diagnostic'
  | 'sales_call_assisted'
  | 'no_sales_call';

export interface KickstartBlueprint {
  _id: string;
  user_id: string;
  enrollment_id?: string | null;
  status: 'draft' | 'accepted' | 'superseded';
  recommended_wrapper: KickstartBlueprintWrapper;
  buyer_context: {
    what_they_sell: string;
    current_acquisition_model: string;
    biggest_bottleneck: string;
    audience_one_liner: string;
  };
  recommendation: {
    headline: string;
    why_this_wrapper: string;
    first_build_scope: string[];
    not_doing_first: string[];
  };
  bridge_personalization: {
    hero_promise: string;
    proof_angle: string;
    objection_to_answer: string;
    next_step_label: string;
  };
  generation_provenance: {
    source: 'os_assembled' | 'ai_synthesized' | 'build_room_intake';
    model?: string | null;
    generated_at: string;
  };
  accepted_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ComputeFromSubmissionsResult {
  id: string;
  status: 'draft';
  deduplicated: boolean;
}

export interface AcceptBlueprintResult {
  id: string;
  status: 'accepted';
  deduplicated: boolean;
}

/** Read the current user's blueprint (accepted preferred, otherwise newest draft). */
export async function getCurrentBlueprint(token?: string | null) {
  return runConvexQuery<KickstartBlueprint | null>(
    'kickstartBlueprint:getCurrentBlueprint',
    {},
    token,
  );
}

/** Read a blueprint resolved by the bridge resume flow (server validates the token). */
export async function getBlueprintForBridge(
  blueprintId: string,
  userId: string,
  token?: string | null,
) {
  return runConvexQuery<KickstartBlueprint | null>(
    'kickstartBlueprint:getBlueprintForBridge',
    { blueprintId, userId },
    token,
  );
}

/**
 * Compute (or return the existing draft of) the AI Discovery Blueprint from the user's
 * 5 lab work submissions. Throws an explicit error if any required submission is missing,
 * which the caller surfaces as a "Finish lesson N first" guard.
 */
export async function computeBlueprintFromSubmissions(
  requiredLessonSlugs: string[],
  token?: string | null,
) {
  return runConvexMutation<ComputeFromSubmissionsResult>(
    'kickstartBlueprint:computeFromSubmissions',
    { requiredLessonSlugs },
    token,
  );
}

/** Mark a draft blueprint as accepted. Idempotent. */
export async function acceptBlueprint(blueprintId: string, token?: string | null) {
  return runConvexMutation<AcceptBlueprintResult>(
    'kickstartBlueprint:acceptBlueprint',
    { blueprintId },
    token,
  );
}

/** Persist a single lesson's lab work for the current user's active enrollment. */
export interface SubmitLabWorkResult {
  id: string;
  lessonSlug: string;
  userId: string;
  content: unknown;
  submittedAt: string;
}

export async function submitCurrentLabWork(
  lessonSlug: string,
  content: unknown,
  token?: string | null,
) {
  return runConvexMutation<SubmitLabWorkResult>(
    'kickstart:submitCurrentLabWork',
    { lessonSlug, content },
    token,
  );
}

/** The 5 V2 lesson slugs that must all be submitted before a blueprint can be computed. */
export const V2_REQUIRED_LESSON_SLUGS = ['01', '02', '03', '04', '05'] as const;
